'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';

import { colors } from '@/styles';
import * as Icon from '@/ui/Icon';

import * as Styled from './styled';

const roundTime = (time) => Math.round(time + Number.EPSILON);

const formatMinutesSeconds = (seconds) => {
  return (seconds - (seconds %= 60)) / 60 + (9 < seconds ? ':' : ':0') + seconds;
};

// WaveSurfer hook
const useWavesurfer = (containerRef, options) => {
  const [wavesurfer, setWavesurfer] = useState(null);

  // Initialize wavesurfer when the container mounts
  // or any of the props change
  useEffect(() => {
    if (!containerRef.current) return;

    const ws = WaveSurfer.create({
      ...options,
      container: containerRef.current,
    });
    setWavesurfer(ws);

    return () => {
      ws.destroy();
    };
  }, [options, containerRef]);

  return wavesurfer;
};

const WaveSurferPlayer = (props) => {
  const containerRef = useRef();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const wavesurfer = useWavesurfer(containerRef, props);

  // On play button click
  const onPlayClick = useCallback(() => {
    wavesurfer.isPlaying() ? wavesurfer.pause() : wavesurfer.play();
  }, [wavesurfer]);

  // Initialize wavesurfer when the container mounts
  // or any of the props change
  useEffect(() => {
    if (!wavesurfer) return;

    setCurrentTime(0);
    setIsPlaying(false);

    const subscriptions = [
      wavesurfer.on('load', () => setIsLoading(true)),
      wavesurfer.on('decode', (d) => setDuration(roundTime(d))),
      wavesurfer.on('ready', () => setIsLoading(false)),
      wavesurfer.on('play', () => setIsPlaying(true)),
      wavesurfer.on('pause', () => setIsPlaying(false)),
      wavesurfer.on('timeupdate', (currentTime) => setCurrentTime(roundTime(currentTime))),
    ];

    return () => {
      subscriptions.forEach((unsub) => unsub());
    };
  }, [wavesurfer]);

  return (
    <Styled.WaveSurferPlayer>
      <Styled.PlayerWindow ref={containerRef}>
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div>Loading...</div>
          </div>
        )}
      </Styled.PlayerWindow>

      <Styled.AudioControl>
        <Styled.PausePlayButton onClick={onPlayClick}>
          {isPlaying ? <Icon.Pause size={30} /> : <Icon.Play size={20} />}
        </Styled.PausePlayButton>

        {/* <pre>{JSON.stringify(isLoading, null, 2)}</pre> */}

        <Styled.DurationIndicator>
          <span>{formatMinutesSeconds(currentTime)}</span>
          <span>/</span>
          <span>{formatMinutesSeconds(duration)}</span>
        </Styled.DurationIndicator>
      </Styled.AudioControl>
    </Styled.WaveSurferPlayer>
  );
};

export default WaveSurferPlayer;
